import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import type { ContactActivityTracking } from '../queries/contactSummaryQuery';
import { GET_CONTACT_SUMMARY } from '../queries/contactSummaryQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof ContactActivityTracking;
  isDate?: boolean;
  isNumber?: boolean;
}

const ACTIVITY_TRACKING_FIELDS: FieldMapping[] = [
  { apiField: 'totalActivities', formField: 'totalActivities', isNumber: true },
  { apiField: 'totalNotes', formField: 'totalNotes', isNumber: true },
  {
    apiField: 'legacyTotalScore',
    formField: 'legacyTotalScore',
    isNumber: true
  },
  {
    apiField: 'lastActivityAddedOn',
    formField: 'lastActivityAddedOn',
    isDate: true
  },
  { apiField: 'lastNoteAddedOn', formField: 'lastNoteAddedOn', isDate: true },
  {
    apiField: 'lastSynchedToNotion',
    formField: 'lastSynchedToNotion',
    isDate: true
  }
];

function getChangedValues(
  fields: FieldMapping[],
  current: ContactActivityTracking,
  initial: ContactActivityTracking
): Record<string, null | number | string> {
  const changes: Record<string, null | number | string> = {};

  for (const { apiField, formField, isDate, isNumber } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    let hasChanged = false;
    if (isDate) {
      hasChanged =
        toDateInputValue(currentVal as string) !==
        toDateInputValue(initialVal as string);
    } else {
      hasChanged = currentVal !== initialVal;
    }

    if (hasChanged) {
      if (isNumber) {
        changes[apiField] = currentVal as null | number;
      } else if (isDate) {
        changes[apiField] = currentVal === '' ? null : (currentVal as string);
      } else {
        changes[apiField] = currentVal === '' ? null : (currentVal as string);
      }
    }
  }

  return changes;
}

function toDateInputValue(isoDate: string): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return isNaN(date.getTime()) ? isoDate : date.toISOString().split('T')[0];
}

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        totalActivities
        totalNotes
        legacyTotalScore
        lastActivityAddedOn
        lastNoteAddedOn
        lastSynchedToNotion
      }
    }
  }
`;

export interface EditActivityTrackingDialogProps {
  contactId: string;
  initialValue: ContactActivityTracking;
  isOpen: boolean;
  onChange: (data: ContactActivityTracking) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactActivityTracking;
}

export function EditActivityTrackingDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditActivityTrackingDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [
      { query: GET_CONTACT_SUMMARY, variables: { id: contactId } }
    ]
  });

  const changes = getChangedValues(
    ACTIVITY_TRACKING_FIELDS,
    value,
    initialValue
  );
  const isDirty = Object.keys(changes).length > 0;

  const handleNumberChange = (
    field: keyof ContactActivityTracking,
    raw: string
  ) => {
    const num = raw.trim() === '' ? null : Number(raw);
    onChange({
      ...value,
      [field]: num === null || Number.isNaN(num) ? null : num
    });
  };

  const formatNumber = (val: null | number) =>
    val === null ? '' : String(val);

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save activity tracking');
      } else {
        toast.success('Activity tracking saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving activity tracking:', error);
      toast.error('Failed to save activity tracking');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Activity Tracking"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditFormField
          label="Total Activities"
          onChange={raw => {
            handleNumberChange('totalActivities', raw);
          }}
          type="number"
          value={formatNumber(value.totalActivities)}
        />
        <EditFormField
          label="Total Notes"
          onChange={raw => {
            handleNumberChange('totalNotes', raw);
          }}
          type="number"
          value={formatNumber(value.totalNotes)}
        />
        <EditFormField
          label="Legacy Total Score"
          onChange={raw => {
            handleNumberChange('legacyTotalScore', raw);
          }}
          type="number"
          value={formatNumber(value.legacyTotalScore)}
        />
        <EditFormField
          label="Last Activity Added On"
          onChange={lastActivityAddedOn => {
            onChange({ ...value, lastActivityAddedOn });
          }}
          type="date"
          value={toDateInputValue(value.lastActivityAddedOn)}
        />
        <EditFormField
          label="Last Note Added On"
          onChange={lastNoteAddedOn => {
            onChange({ ...value, lastNoteAddedOn });
          }}
          type="date"
          value={toDateInputValue(value.lastNoteAddedOn)}
        />
        <EditFormField
          label="Last Synched to Notion"
          onChange={lastSynchedToNotion => {
            onChange({ ...value, lastSynchedToNotion });
          }}
          type="date"
          value={toDateInputValue(value.lastSynchedToNotion)}
        />
      </div>
    </EditDialog>
  );
}
